import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  REALLOCATION_V2_DO_NOT_TRADE_STEP_ENABLED,
  ReallocationV2Store,
} from '../../state/reallocation-v2.store';

/** Step 5 — read-only summary of the whole request before submitting. */
@Component({
  selector: 'app-rav2-review-submit-step',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './review-submit-step.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReviewSubmitStepComponent {
  readonly store = inject(ReallocationV2Store);

  /**
   * Whether to show the Do Not Trade column on the Custom Holdings table.
   *
   * Tied to the step's flag rather than to whether anything is marked: with the step off
   * nothing can be, so the column would be a full column of em-dashes for a concept the
   * advisor was never offered -- which reads as a defect rather than as "none selected".
   */
  readonly showDoNotTrade = REALLOCATION_V2_DO_NOT_TRADE_STEP_ENABLED;

  /** "CONTRIBUTION FUNDING" / "DISTRIBUTION FUNDING", for the section header. */
  fundingHeader(): string {
    return this.store.isContribution() ? 'Contribution Funding' : 'Distribution Funding';
  }

  /** How the sleeve money is described — into the models, or out of them. */
  sleeveHeader(): string {
    return this.store.isContribution() ? 'Contribution to Models' : 'Distribution from Models';
  }

  /** The amount row's caption, which is the whole request in one line. */
  amountLabel(): string {
    if (this.store.isCloseAccount()) return 'Full account value';
    return this.store.isContribution() ? 'Contribution amount' : 'Gross amount';
  }

  /**
   * The frequency, plus its start date when it has one.
   *
   * One line rather than two rows: a schedule is a single fact, and "Weekly" with the date on a
   * separate row three lines down reads as two unrelated settings.
   */
  scheduleLabel(): string {
    const frequency = this.store.selectedDeliveryFrequency()?.name ?? '—';
    const start = this.store.fundingForm.controls.startDate.value;
    if (!this.store.startDateRequired() || start == null) return frequency;
    return `${frequency}, starting ${start.toLocaleDateString('en-US')}`;
  }

  /**
   * The chosen bank instruction, or why there is none.
   *
   * ⚠️ "Not applicable" rather than a dash for the methods that use no bank instruction: an
   * em-dash on a request that never needed one reads as a value that failed to load.
   */
  bankLabel(): string {
    if (!this.store.usesBankInstruction()) return 'Not applicable for this method';
    return this.store.selectedStandingInstruction()?.displayValue ?? '—';
  }

  /** Withholding as a single line; '—' when neither is set. */
  withholdingLabel(): string {
    const fed = this.store.federalTaxPct();
    const state = this.store.stateTaxPct();
    if (fed === 0 && state === 0) return 'None';
    return `Federal ${fed.toFixed(2)}% · State ${state.toFixed(2)}%`;
  }
}
